import { IProduct } from 'modules/inventory/product/interfaces/IProduct';

/**
 * ¿Este producto es un combo, es decir, tiene su stock respaldado por reservas
 * de componentes y NO por una cifra que alguien teclea?
 *
 * Réplica exacta del predicado que ya decide esto en el backend:
 * `ms-inventory/src/modules/product-management/products/combo/is-combo-product.util.ts`.
 * Allí es la puerta que rechaza una escritura de stock por la vía genérica con
 * `COMBO_STOCK_NOT_DIRECTLY_MANAGEABLE`; aquí es la que decide si esa pantalla
 * llega siquiera a ofrecer el formulario. Si los dos criterios se separan, la
 * UI vuelve a ofrecer un campo que el servidor va a rechazar — que es
 * exactamente el bug que esto arregla. Cualquier cambio en el de allá tiene que
 * bajar aquí.
 *
 * **Por qué no basta `combo.enabled`.** `enabled` es el interruptor de VENTA,
 * no el de existencia. El backend (`ComboResyncService`) lo apaga solo cuando
 * detecta la composición rota — un componente borrado, o que cambió de
 * supplier — y **no desarma nada**: el combo se queda con sus `items` y con
 * todo su stock armado, respaldado por reservas vivas. Un combo apagado por
 * avería es justo el caso en el que un admin va a intentar «arreglarlo»
 * añadiéndole stock a mano, y sigue siendo tan virtual como antes. Por eso la
 * composición (`items`) pesa igual que el interruptor.
 *
 * **Por qué tampoco basta con que exista el objeto `combo`.** Un ex-combo que
 * una persona desactivó queda con `{enabled: false, items: []}` y sin una sola
 * reserva viva (el backend desarma todo antes de guardar). Ese producto ya es
 * un producto normal: su stock se teclea como el de cualquier otro, y
 * bloquearle el formulario lo dejaría sin ninguna vía para tenerlo — ni la
 * genérica ni la de combos, que sin componentes no arma nada.
 */
export const isComboProduct = (product?: Partial<IProduct> | null): boolean => {
  const combo = product?.combo;
  if (!combo) return false;
  return combo.enabled || (combo.items?.length ?? 0) > 0;
};
